import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { environment } from "src/environments/environment";

const SESSION_KEY = "ctms_visitor_session";

@Injectable({ providedIn: "root" })
export class VisitTrackerService {
  private lastPath = "";
  private lastAt = 0;

  constructor(private router: Router) {}

  start(): void {
    this.track(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.track(event.urlAfterRedirects || event.url));
  }

  private track(rawUrl: string): void {
    const path = this.normalizePath(rawUrl);
    if (!path || path === "/analytics") return;

    const now = Date.now();
    if (path === this.lastPath && now - this.lastAt < 2000) return;
    this.lastPath = path;
    this.lastAt = now;

    const body = {
      path,
      sessionId: this.sessionId(),
      referrer: typeof document !== "undefined" ? document.referrer : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      language: typeof navigator !== "undefined" ? navigator.language : "",
      screen:
        typeof window !== "undefined"
          ? `${window.screen?.width || 0}x${window.screen?.height || 0}`
          : "",
    };

    fetch(`${environment.apiUrl}/analytics/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  }

  private normalizePath(url: string): string | null {
    const path = String(url || "").split("#")[0].split("?")[0].trim() || "/";
    if (!path.startsWith("/")) return `/${path}`;
    return path;
  }

  private sessionId(): string {
    try {
      const existing = localStorage.getItem(SESSION_KEY);
      if (existing && /^[a-zA-Z0-9-]{8,64}$/.test(existing)) {
        return existing;
      }
      const created =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(SESSION_KEY, created);
      return created;
    } catch {
      return `v-${Date.now().toString(36)}`;
    }
  }
}
